import { Component, OnInit } from '@angular/core'
import { LogEmail } from '@parkupp/core'
import { AdminService } from '../../../services/admin.service'
import { LogEmailService } from 'src/app/services/log-email.service'

@Component({
    selector: 'app-list-emails-page',
    templateUrl: './list-emails-page.component.html',
    styleUrls: ['./list-emails-page.component.scss'],
})
export class ListEmailsPageComponent implements OnInit {
    logEmails: LogEmail[] = []
    filteredEmails: LogEmail[] = []
    loading = true
    Math = Math

    // Pagination
    currentPage = 1
    itemsPerPage = 200
    totalItems = 0
    totalPages = 0

    // Search / Filter
    searchTerm = ''
    filterType: 'all' | 'needs-review' = 'all'

    constructor(private adminService: AdminService, private logEmailService: LogEmailService) {}

    async ngOnInit(): Promise<void> {
        this.loading = true
        this.logEmails = await this.logEmailService.getAllEmails()
        this.applyFilters()
        this.loading = false
    }

    applyFilters() {
        let filtered = [...this.logEmails]

        if (this.searchTerm.trim()) {
            const searchLower = this.searchTerm.toLowerCase()
            filtered = filtered.filter((email) => {
                const toEmailsMatch = email.toEmails?.some((addr) => addr && addr.toLowerCase().includes(searchLower))
                const subjectMatch = email.subject && email.subject.toLowerCase().includes(searchLower)
                const contentMatch = email.content && email.content.toLowerCase().includes(searchLower)
                return toEmailsMatch || subjectMatch || contentMatch
            })
        }

        if (this.filterType === 'needs-review') {
            filtered = filtered.filter((email) => email.needsReview)
        }

        this.filteredEmails = filtered
        this.totalItems = filtered.length
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage)

        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = 1
        }
    }

    onSearchChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    onFilterTypeChange(type: 'all' | 'needs-review') {
        this.filterType = type
        this.currentPage = 1
        this.applyFilters()
    }

    onItemsPerPageChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    getPaginatedEmails(): LogEmail[] {
        const startIndex = (this.currentPage - 1) * this.itemsPerPage
        const endIndex = startIndex + this.itemsPerPage
        return this.filteredEmails.slice(startIndex, endIndex)
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = []
        const maxPagesToShow = 5
        const halfRange = Math.floor(maxPagesToShow / 2)

        let startPage = Math.max(1, this.currentPage - halfRange)
        let endPage = Math.min(this.totalPages, this.currentPage + halfRange)

        if (endPage - startPage + 1 < maxPagesToShow) {
            if (startPage === 1) {
                endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1)
            } else if (endPage === this.totalPages) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1)
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return pages
    }

    clearSearch() {
        this.searchTerm = ''
        this.onSearchChange()
    }

    async onNeedsReview(logEmail: LogEmail): Promise<void> {
        logEmail.needsReview = !logEmail.needsReview
        await this.logEmailService.updateOverviewDoc(logEmail)
    }

    getNeedsReviewCount(): number {
        return this.logEmails.filter((e) => e.needsReview).length
    }

    getRecipients(logEmail: LogEmail): string {
        if (!logEmail.toEmails || logEmail.toEmails.length === 0) return 'N/A'
        return logEmail.toEmails.join(', ')
    }

    trackByEmailKey(index: number, logEmail: LogEmail): string {
        return logEmail.$key
    }
}
